/**
 * Opportunistic device-position capture on authed web pages (docs#10).
 *
 * When the browser's geolocation permission is already granted, capture one snapped fix and POST
 * it as a non-signup observation. Never prompts: anything other than 'granted' captures nothing.
 * Browsers without the Permissions API (older Safari) are skipped, and their device point expires
 * on the server after about 30 days.
 *
 * The main layout opts its pages in with these meta tags. Funnel/signup pages (_funnel.twig)
 * render none of them, because the signup wizard has its own capture path.
 * - `geo-observation`: the opt-in.
 * - `geo-capture-interval-s`: the server's capture interval for this member.
 * - `geo-point-eligible`: 1 when the member can have a device point. Only then does a change of
 *   the permission state reach the server, so that a lost permission freezes the point.
 *
 * Throttle: at most one capture per user per interval, tracked in localStorage keyed by user id.
 * The timestamp is written before the async capture starts. Tabs that load at the same moment can
 * each capture once (localStorage has no atomic check-and-set), and the endpoint's per-user rate
 * limit bounds that race. The last posted permission status sits beside the throttle key.
 *
 * Everything is wrapped so a failure here can never break page load.
 */

import apiClient from './api-client.js';
import { hasGeolocation, queryGeolocationPermission, requestSnappedPosition } from './location/device-position.js';

const DEFAULT_INTERVAL_S = 7 * 24 * 60 * 60;
const THROTTLE_KEY_PREFIX = 'geoObservation:lastCapture:';
const STATUS_KEY_PREFIX = 'geoObservation:lastStatus:';
const POSITION_TIMEOUT_MS = 15000;
// The prompt kinds of WebLocationClientView::WEB_PROMPT_KINDS.
export const WEB_CLIENT_CAPS = ['city_auto_notice', 'city_nudge'];
// A fix from the last hour is fresh enough and avoids spinning up positioning hardware when the
// browser has one cached.
const POSITION_MAX_AGE_MS = 60 * 60 * 1000;

/** Permissions API state to the permission_status the endpoint accepts. */
const PERMISSION_STATUS = {
    granted: 'granted',
    denied: 'denied',
    prompt: 'prompt_dismissed',
};

/**
 * Attempt one opportunistic capture. Safe to call on every page load.
 * Fire-and-forget: never throws, never rejects.
 */
export async function maybeCaptureGeoObservation() {
    try {
        if (!document.querySelector('meta[name="geo-observation"]')) {
            return;
        }

        const userId = metaContent('user-id');
        if (!userId || !hasGeolocation()) {
            return;
        }

        const state = await queryGeolocationPermission();
        const status = state !== null ? PERMISSION_STATUS[state] : undefined;
        if (status === undefined) {
            return;
        }

        const statusKey = STATUS_KEY_PREFIX + userId;
        const lastStatus = readStorage(statusKey);
        if (status !== 'granted') {
            if (metaContent('geo-point-eligible') === '1' && status !== lastStatus) {
                const response = await apiClient.postGeoObservation({ permission_status: status }, { signup_context: false, client_caps: WEB_CLIENT_CAPS });
                // A failed post leaves the old status, so the next page load posts again.
                if (response && response.ok) {
                    writeStorage(statusKey, status);
                }
            }
            return;
        }

        // A permission granted again after a loss captures at once, so that the freeze lifts.
        const regained = lastStatus === 'denied' || lastStatus === 'prompt_dismissed';
        if (!regained && !claimThrottleSlot(THROTTLE_KEY_PREFIX + userId, captureIntervalMs())) {
            return;
        }
        if (regained) {
            // Recorded before the capture, so a failing position request waits for the next interval.
            writeStorage(THROTTLE_KEY_PREFIX + userId, String(Date.now()));
            writeStorage(statusKey, 'granted');
        }

        const position = await requestSnappedPosition({
            timeout: POSITION_TIMEOUT_MS,
            maximumAge: POSITION_MAX_AGE_MS,
        });

        const response = await apiClient.postGeoObservation(
            { permission_status: 'granted', ...position },
            { signup_context: false, client_caps: WEB_CLIENT_CAPS }
        );
        if (response && response.ok) {
            writeStorage(statusKey, 'granted');
        }
    } catch {
        // Denied-after-query races, positioning failures, storage or network errors: all
        // intentionally swallowed. The throttle slot stays claimed so a persistent failure
        // retries on the next window, not on every page load.
    }
}

/**
 * Records the signup observation as this member's last capture, so the first opportunistic
 * capture waits a full interval (privacy clause 5.17). The status stays unset, because a stored
 * lost status makes a later grant capture at once.
 */
export function recordSignupCapture(userId) {
    writeStorage(THROTTLE_KEY_PREFIX + userId, String(Date.now()));
}

function metaContent(name) {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? meta.getAttribute('content') : null;
}

function captureIntervalMs() {
    const seconds = parseInt(metaContent('geo-capture-interval-s'), 10);
    return (Number.isNaN(seconds) || seconds <= 0 ? DEFAULT_INTERVAL_S : seconds) * 1000;
}

function readStorage(key) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Storage unavailable: the next page load posts again, and the rate limit bounds it.
    }
}

/**
 * True when no capture ran within the interval. Claims the slot by writing the current timestamp
 * before any async work, so within one tab a window admits a single attempt.
 */
function claimThrottleSlot(storageKey, intervalMs) {
    try {
        const last = parseInt(window.localStorage.getItem(storageKey), 10);
        if (!Number.isNaN(last) && Date.now() - last < intervalMs) {
            return false;
        }
        window.localStorage.setItem(storageKey, String(Date.now()));
        return true;
    } catch {
        // localStorage unavailable (private mode, quota): without a throttle we must not capture
        // on every page load.
        return false;
    }
}
